import React from "react";
import { useSearchParams } from "react-router";
import { Calculator, Layers, MapPin, PlusCircle, Route, Settings2 } from "lucide-react";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import LoadingScreen from "../../../utils/LoadingScreen";
import { usePricing } from "../../../contexts/PricingContext";
import PageHeader from "../../components/pricing/PageHeader";
import EmptyPricingState from "../../components/pricing/EmptyPricingState";
import ZonesTab from "../../components/pricing/ZonesTab";
import PlacesTab from "../../components/pricing/PlacesTab";
import RoutesTab from "../../components/pricing/RoutesTab";
import ExtrasTab from "../../components/pricing/ExtrasTab";
import ConfigTab from "../../components/pricing/ConfigTab";
import SimulatorTab from "../../components/pricing/SimulatorTab";
import { Title } from "../../components/settings/Title";

const TABS = [
  { id: "zones", label: "Regiões", icon: Layers, content: ZonesTab },
  { id: "places", label: "Locais", icon: MapPin, content: PlacesTab },
  { id: "routes", label: "Tarifas", icon: Route, content: RoutesTab },
  { id: "extras", label: "Adicionais", icon: PlusCircle, content: ExtrasTab },
  { id: "config", label: "Config", icon: Settings2, content: ConfigTab },
  { id: "simulate", label: "Simular", icon: Calculator, content: SimulatorTab },
] as const;

type TabId = (typeof TABS)[number]["id"];

export default function PricingPage() {
  const { loading, error, hasData } = usePricing();
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("tab");
  const activeTab: TabId = TABS.some((t) => t.id === requested) ? (requested as TabId) : "zones";

  if (loading) return <LoadingScreen />;

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-6 pb-16 text-white">
      <PageHeader eyebrow="Outros" title="Tabela de preços" backTo="/admin-management/menu" />

      {error && (
        <p className="mb-4 rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
          Não foi possível carregar a tabela de preços. Verifique a conexão e tente novamente.
        </p>
      )}

      {!hasData ? (
        <div className="space-y-6">
          <EmptyPricingState />
          <section className="space-y-3">
            <Title>Configurações</Title>
            <ConfigTab />
          </section>
        </div>
      ) : (
        <Tabs value={activeTab} onValueChange={(tab) => setSearchParams({ tab }, { replace: true })}>
          <div className="-mx-4 overflow-x-auto px-4 pb-2">
            <TabsList className="h-auto w-max gap-1 bg-white/5 p-1 text-white/60">
              {TABS.map((tab) => (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className="h-9 gap-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          {TABS.map((tab) => (
            <TabsContent key={tab.id} value={tab.id} className="mt-4 space-y-3">
              <Title>{tab.label}</Title>
              <tab.content />
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
}
